import { Injectable } from '@angular/core';
import { ConfigurationsService } from '@epicuro-next/platform/configuration';
import { LoggerService } from '@epicuro-next/platform/monitoring';
import {
  selectActiveAccountId,
  setActiveAccount,
} from '@epicuro-next/state/accounts-state';
import { selectBasketTotals } from '@epicuro-next/state/basket-state';
import { isEqual } from '@epicuro-next/utilities';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, select, Store } from '@ngrx/store';
import { Total } from 'graphql-api/baskets-api-interfaces';
import { iif, of } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  mergeMap,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';

import {
  DabConfigurationKeys,
  DabStateConfigurations,
  DAB_FEATURE_NAME,
} from '../configurations';
import { DabService } from '../services/dab.service';

import { selectAllDABs } from './dab-items.selectors';
import * as dabActions from './dab.actions';
import { updateDabWithBasketItems } from './update-dab-with-basket-items';

/**
 * Dab Effects
 */
@Injectable()
export class DabEffects {
  /**
   * Effect to load the filters in the p-basket
   */
  public loadDab$ = createEffect(() =>
    this.actions$.pipe(
      ofType(dabActions.loadDab),
      withLatestFrom(
        this.store.pipe(select(selectActiveAccountId), filter(Boolean)),
        this.configService.getConfigValue<
          DabStateConfigurations,
          DabConfigurationKeys.DabTurned
        >({
          namespace: DAB_FEATURE_NAME,
          featureName: DabConfigurationKeys.DabTurned,
          defaultValue: true,
        }),
        this.configService.getConfigValue<
          DabStateConfigurations,
          DabConfigurationKeys.UseLegacyAggregationApi
        >({
          namespace: DAB_FEATURE_NAME,
          featureName: DabConfigurationKeys.UseLegacyAggregationApi,
          defaultValue: true,
        }),
      ),
      filter(([, , dabTurned]) => !!dabTurned),
      mergeMap(([action, , , useLegacyAggregation]) =>
        this.dabService
          .getDab({
            products: action.products.map((product) => ({
              productId: product.productId,
              brandId: product.brandId,
            })),
            useLegacyAggregation,
          })
          .pipe(
            take(1),
            switchMap(({ dab }) =>
              iif<Action, Action>(
                () => useLegacyAggregation,
                of(
                  dabActions.loadDabSuccess({
                    dabData: dab ?? [],
                  }),
                ),
                this.store.pipe(
                  select(selectBasketTotals),
                  take(1),
                  map((total: Total | undefined) =>
                    dabActions.loadDabSuccess({
                      dabData: updateDabWithBasketItems(
                        total?.products ?? [],
                        dab,
                      ),
                    }),
                  ),
                ),
              ),
            ),
            catchError((res: { message: string }) =>
              of(
                dabActions.loadDabFailure({
                  error: res.message,
                }),
              ),
            ),
          ),
      ),
    ),
  );

  /**
   * Effect to signal failure patching a product list
   */
  public loadDabFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(dabActions.loadDabFailure),
        tap((action) => {
          this.loggerService.exception({
            error: new Error(action.error),
          });
        }),
      ),
    {
      dispatch: false,
    },
  );

  public clearDab$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setActiveAccount),
      map(() => dabActions.clearDab()),
    ),
  );

  public updateDabOnBasketChange$ = createEffect(() =>
    this.store.pipe(
      select(selectBasketTotals),
      map((total) => total?.products ?? []),
      distinctUntilChanged(isEqual),
      withLatestFrom(this.store.select(selectAllDABs)),
      map(([products, dabData]) =>
        dabActions.updateDab({
          dabData: updateDabWithBasketItems(products, dabData),
        }),
      ),
    ),
  );

  public loadDabHistory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(dabActions.loadDabHistory),
      concatLatestFrom(() => [
        this.store.pipe(select(selectActiveAccountId), filter(Boolean)),
        this.configService.getConfigValue({
          namespace: DAB_FEATURE_NAME,
          featureName: DabConfigurationKeys.InboundDetailsTurnedOn,
          defaultValue: false,
        }),
      ]),
      filter(([, , inboundDetailsTurnedOn]) => !!inboundDetailsTurnedOn),
      mergeMap(([action]) =>
        this.dabService
          .getDabHistory({
            products: action.products.map((product) => ({
              productId: product.productId,
              brandId: product.brandId,
            })),
          })
          .pipe(
            take(1),
            map((queryResult) =>
              dabActions.loadDabHistorySuccess({
                dabHistoryData: queryResult.dabHistory ?? [],
              }),
            ),
            catchError((res: { message: string }) =>
              of(
                dabActions.loadDabHistoryFailure({
                  error: res.message,
                }),
              ),
            ),
          ),
      ),
    ),
  );

  public loadDabHistoryFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(dabActions.loadDabHistoryFailure),
        tap(({ error }) => {
          this.loggerService.exception({
            error: new Error(error),
          });
        }),
      ),
    {
      dispatch: false,
    },
  );

  /**
   * Dependencies
   * @param actions$ Action dispatched
   * @param productBasketService Product basket service
   * @param store P-basket store
   */
  constructor(
    private actions$: Actions,
    private dabService: DabService,
    private store: Store,
    private loggerService: LoggerService,
    private configService: ConfigurationsService,
  ) {}
}
